/**
 * Utility functions for property calculations that handle different data structures safely
 */

/**
 * Safely extract renovation costs regardless of data structure
 * Handles both object format and numeric format from test data
 */
export function getRenovationCosts(renovationCosts: any): number {
  if (!renovationCosts) return 0

  // If it's already a number (as in test data), return it
  if (typeof renovationCosts === 'number') {
    return isNaN(renovationCosts) ? 0 : renovationCosts
  }

  // If it's an object with cost breakdown, sum the values
  if (typeof renovationCosts === 'object' && renovationCosts !== null) {
    const total = Object.values(renovationCosts).reduce((sum: number, cost: unknown) => {
      const numericCost = typeof cost === 'number' ? cost : 0
      return sum + (isNaN(numericCost) ? 0 : numericCost)
    }, 0)
    return isNaN(total) ? 0 : total
  }

  return 0
}

/**
 * Calculate monthly mortgage payment with input validation
 */
export function calculateMonthlyPayment(loanAmount: number, annualInterestRate: number, loanTermYears: number): number {
  // Validate inputs
  if (!loanAmount || isNaN(loanAmount) || loanAmount <= 0) return 0
  if (!annualInterestRate || isNaN(annualInterestRate) || annualInterestRate < 0) return 0
  if (!loanTermYears || isNaN(loanTermYears) || loanTermYears <= 0) return 0

  const monthlyInterestRate = annualInterestRate / 100 / 12
  const numberOfPayments = loanTermYears * 12

  if (monthlyInterestRate === 0) {
    return Math.round(loanAmount / numberOfPayments)
  }

  const monthlyPayment = loanAmount *
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)

  const result = Math.round(monthlyPayment)
  return isNaN(result) ? 0 : result
}

/**
 * Safe number conversion with fallback
 */
export function safeNumber(value: any, fallback: number = 0): number {
  if (value === null || value === undefined) return fallback
  const num = typeof value === 'number' ? value : parseFloat(value)
  return isNaN(num) ? fallback : num
}

/**
 * Check if renovation costs exist and have any non-zero values
 */
export function hasRenovationCosts(renovationCosts: any): boolean {
  if (!renovationCosts) return false

  // If it's a number, check if it's greater than 0
  if (typeof renovationCosts === 'number') {
    return !isNaN(renovationCosts) && renovationCosts > 0
  }

  // If it's an object, check if any values are greater than 0
  if (typeof renovationCosts === 'object' && renovationCosts !== null) {
    return Object.values(renovationCosts).some((cost: any) => {
      const numericCost = typeof cost === 'number' ? cost : 0
      return !isNaN(numericCost) && numericCost > 0
    })
  }

  return false
}

/**
 * Get renovation costs entries for display
 */
export function getRenovationCostEntries(renovationCosts: any): Array<[string, number]> {
  if (!renovationCosts) return []

  // If it's a number, return a single "total" entry
  if (typeof renovationCosts === 'number') {
    return isNaN(renovationCosts) || renovationCosts <= 0 ? [] : [['Total', renovationCosts]]
  }

  // If it's an object, return all non-zero entries
  if (typeof renovationCosts === 'object' && renovationCosts !== null) {
    return Object.entries(renovationCosts)
      .filter(([_, cost]: [string, any]) => {
        const numericCost = typeof cost === 'number' ? cost : 0
        return !isNaN(numericCost) && numericCost > 0
      })
      .map(([category, cost]: [string, any]) => [category, typeof cost === 'number' ? cost : 0])
  }

  return []
}